import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { MfaPreferences, RecoveryCode } from '@core/interfaces';
import { environment } from '@environment';
import { chain } from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class MfaService {
  constructor(
    private readonly http: HttpClient,
    private readonly authenticationService: AuthenticationService
  ) { }

  public async getMfaPreferences(): Promise<MfaPreferences> {
    const url = `${environment.MORANNON_URL}/mfa-settings`;
    return this.http.get(url, {
      headers: { 'x-access-token': this.authenticationService.accessToken }
    })
      .toPromise()
      .then((result) => result['preferences']);
  }

  public async regenerateToken({ password }): Promise<string> {
    const url = `${environment.MORANNON_URL}/auth/regenerate-token`;
    const body = { password };

    return this.http.post(url, body, {
      headers: { 'x-access-token': this.authenticationService.accessToken }
    })
      .toPromise()
      .then((result) => result['accessToken']);
  }

  public async generateSecretToken({ accessToken }): Promise<string> {
    const url = `${environment.MORANNON_URL}/mfa-settings/generate`;
    return this.http.post(url, null, {
      headers: { 'x-access-token': accessToken }
    })
      .toPromise()
      .then((result) => result['secretCode']);
  }

  public async verifySecretToken({ code, accessToken }): Promise<void> {
    const url = `${environment.MORANNON_URL}/mfa-settings/verify`;
    const body = { code };

    await this.http.post(url, body, {
      headers: { 'x-access-token': accessToken }
    })
      .toPromise();
  }

  public async getRecoveryCodes({ accessToken }): Promise<RecoveryCode[]> {
    const url = `${environment.MORANNON_URL}/recovery-codes`;
    return this.http.get(url, {
      headers: { 'x-access-token': accessToken }
    })
      .toPromise()
      .then((result) => {
        return chain(result['recoveryCodes'] || [])
          .sortBy(['status', 'plainTextCode'])
          .value();
      });
  }

  public async regenerateRecoveryCodes({ accessToken }): Promise<RecoveryCode[]> {
    const url = `${environment.MORANNON_URL}/recovery-codes/regenerate`;

    return this.http.post(url, null, {
      headers: { 'x-access-token': accessToken }
    })
      .toPromise()
      .then((result) => {
        return chain(result['recoveryCodes'] || [])
          .sortBy(['status', 'plainTextCode'])
          .value();
      });
  }

  public async disableMfa({ accessToken }): Promise<void> {
    const url = `${environment.MORANNON_URL}/mfa-settings/disable`;

    await this.http.post(url, null, {
      headers: { 'x-access-token': accessToken }
    })
      .toPromise();
  }
}
